import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { agentService } from '../../services/agentService';
import { AgentCard } from './AgentCard';
import { InviteAgentModal } from './InviteAgentModal';
import { ConfirmationModal } from '../common/ConfirmationModal';
import type { BusinessAgent, PendingInvitation } from '../../types/agent';

interface AgentsSectionProps {
  businessProfileId?: string;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

export function AgentsSection({
  businessProfileId,
  isCollapsed = false,
  onToggle,
}: AgentsSectionProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [agents, setAgents] = useState<BusinessAgent[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [removingAgent, setRemovingAgent] = useState<BusinessAgent | null>(null);
  const [cancellingInvitation, setCancellingInvitation] = useState<PendingInvitation | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const loadAgents = useCallback(async () => {
    if (!token || !businessProfileId) return;

    setIsLoading(true);
    try {
      const [agentsData, invitationsData] = await Promise.all([
        agentService.getBusinessAgents(businessProfileId, token),
        agentService.getPendingInvitations(businessProfileId, token),
      ]);
      setAgents(agentsData);
      setPendingInvitations(invitationsData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load agents';
      showToast('error', t('common.error'), message);
    } finally {
      setIsLoading(false);
    }
  }, [token, businessProfileId, showToast, t]);

  // Load data on mount to show count in header
  useEffect(() => {
    if (businessProfileId) {
      loadAgents();
    }
  }, [businessProfileId, loadAgents]);

  const handleInvite = () => {
    setIsInviteModalOpen(true);
  };

  const handleRemoveAgent = (agent: BusinessAgent) => {
    setRemovingAgent(agent);
  };

  const handleCancelInvitation = (invitation: PendingInvitation) => {
    setCancellingInvitation(invitation);
  };

  const confirmRemoveAgent = async () => {
    if (!token || !businessProfileId || !removingAgent) return;

    setIsRemoving(true);
    try {
      await agentService.removeAgent(businessProfileId, removingAgent.id, token);
      showToast('success', t('agents.removeSuccess'));
      setAgents(prev => prev.filter(a => a.id !== removingAgent.id));
      setRemovingAgent(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove agent';
      showToast('error', t('common.error'), message);
    } finally {
      setIsRemoving(false);
    }
  };

  const confirmCancelInvitation = async () => {
    if (!token || !cancellingInvitation) return;

    setIsRemoving(true);
    try {
      await agentService.cancelInvitation(cancellingInvitation.id, token);
      showToast('success', t('agents.invitationCancelled'));
      setPendingInvitations(prev => prev.filter(i => i.id !== cancellingInvitation.id));
      setCancellingInvitation(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel invitation';
      showToast('error', t('common.error'), message);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleInviteSuccess = () => {
    setIsInviteModalOpen(false);
    loadAgents();
  };

  const totalCount = agents.length + pendingInvitations.length;

  return (
    <div className="bg-brand-primary-deep rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-brand-primary-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <span className="text-lg font-medium text-white">{t('agents.title')}</span>
          <span className="text-sm text-white/70">({totalCount})</span>
        </div>
        <svg
          className={`w-5 h-5 text-white/70 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-4">
          {/* Invite Button */}
          <button
            onClick={handleInvite}
            disabled={!businessProfileId}
            className="w-full py-3 border-2 border-dashed border-white/30 rounded-lg text-white/80 hover:border-brand-primary-light hover:text-brand-primary-light transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            {t('agents.inviteAgent')}
          </button>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="bg-white/10 rounded-lg p-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 bg-white/20 rounded w-32 mb-2" />
                      <div className="h-3 bg-white/20 rounded w-48" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && agents.length === 0 && pendingInvitations.length === 0 && (
            <div className="text-center py-8 text-white/60">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p>{t('agents.noAgents')}</p>
            </div>
          )}

          {/* Active Agents */}
          {!isLoading && agents.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-white/70">{t('agents.activeAgents')}</h4>
              {agents.map(agent => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  onRemove={() => handleRemoveAgent(agent)}
                />
              ))}
            </div>
          )}

          {/* Pending Invitations */}
          {!isLoading && pendingInvitations.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-white/70">{t('agents.pendingInvitations')}</h4>
              {pendingInvitations.map(invitation => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between bg-surface-card rounded-lg p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-accent/20 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-ink-primary">{invitation.email}</p>
                      <p className="text-xs text-ink-muted">
                        {t('agents.expiresAt', {
                          date: new Date(invitation.expiresAt).toLocaleDateString(),
                        })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCancelInvitation(invitation)}
                    className="px-3 py-1 text-xs bg-brand-warm text-white rounded hover:bg-brand-warm-deep transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {isInviteModalOpen && businessProfileId && (
        <InviteAgentModal
          businessProfileId={businessProfileId}
          onClose={() => setIsInviteModalOpen(false)}
          onSuccess={handleInviteSuccess}
        />
      )}

      {/* Remove Agent Confirmation */}
      {removingAgent && (
        <ConfirmationModal
          title={t('agents.removeConfirmTitle')}
          message={t('agents.removeConfirmMessage', {
            name: `${removingAgent.firstName} ${removingAgent.lastName}`,
          })}
          confirmLabel={t('common.remove')}
          cancelLabel={t('common.cancel')}
          onConfirm={confirmRemoveAgent}
          onCancel={() => setRemovingAgent(null)}
          isLoading={isRemoving}
          variant="danger"
        />
      )}

      {/* Cancel Invitation Confirmation */}
      {cancellingInvitation && (
        <ConfirmationModal
          title={t('agents.cancelInvitationTitle')}
          message={t('agents.cancelInvitationMessage', { email: cancellingInvitation.email })}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('common.cancel')}
          onConfirm={confirmCancelInvitation}
          onCancel={() => setCancellingInvitation(null)}
          isLoading={isRemoving}
          variant="warning"
        />
      )}
    </div>
  );
}
